<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import Loader from "@/helpers/loader";
import Modal from "@/helpers/modal";
import { Date as BrDate } from "@/helpers/date";

export default {
  name: "ChargeNegotiationDetailsModal",
  props: {
    subscriber: { type: Object, default: null },
    placeId: { type: [Number, String], required: true },
  },
  data() {
    return {
      negotiationId: null,
      negotiation: null,
      originCharges: [],
      destinationCharges: [],
    };
  },
  computed: {
    subscriberId() {
      return this.subscriber?.id_subscriber;
    },
  },
  methods: {
    async open(negotiationId) {
      if (!this.subscriberId) {
        Alert.error('Assinante inválido.');
        return;
      }
      if (!negotiationId) {
        Alert.error('Negociação inválida.');
        return;
      }

      this.negotiationId = negotiationId;
      this.negotiation = null;
      this.originCharges = [];
      this.destinationCharges = [];

      Modal.toggle('#chargeNegotiationDetailsModal');
      await this.load();
    },
    close() {
      Modal.toggle('#chargeNegotiationDetailsModal');
      this.negotiationId = null;
      this.negotiation = null;
      this.originCharges = [];
      this.destinationCharges = [];
    },
    async load() {
      Loader.show('Carregando negociação...', 'Aguarde');
      try {
        const response = await http.get(
          `/places/${this.placeId}/subscribers/${this.subscriberId}/charges/negotiations/${this.negotiationId}`
        );
        this.negotiation = response.data?.negotiation;
        this.originCharges = response.data?.origin_charges || [];
        this.destinationCharges = response.data?.destination_charges || [];
      } catch (e) {
        Alert.error(e.response?.data?.message || e.response?.data?.error || 'Erro ao carregar detalhes da negociação.');
      } finally {
        Loader.close();
      }
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2).replace('.', ',');
    },
    formatDate(date) {
      return BrDate.formatLocalDate(date);
    },
    statusBadgeClass(status) {
      switch (status) {
        case 'paid': return 'badge bg-success';
        case 'pending': return 'badge bg-warning text-dark';
        case 'overdue': return 'badge bg-danger';
        case 'cancelled': return 'badge bg-info';
        case 'payment_failed': return 'badge bg-dark';
        case 'negotiated': return 'badge bg-secondary';
        default: return 'badge bg-secondary';
      }
    },
  },
}
</script>

<template>
  <div class="modal fade" id="chargeNegotiationDetailsModal" tabindex="-1" data-bs-keyboard="false"
       data-bs-backdrop="static" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Detalhes da negociação #{{ negotiationId }}</h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>

        <div class="modal-body">
          <div v-if="negotiation" class="mb-4">
            <div class="row g-3">
              <div class="col-md-3">
                <div class="text-muted">Data</div>
                <div class="fw-bold">{{ formatDate(negotiation.negotiation_date) }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Responsável</div>
                <div class="fw-bold">{{ negotiation.user?.user_name || '-' }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Cliente</div>
                <div class="fw-bold">{{ negotiation.customer?.customer_name || '-' }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Pagamento</div>
                <div class="fw-bold">{{ negotiation.payment_method || '-' }}</div>
              </div>
            </div>

            <div class="row g-3 mt-2">
              <div class="col-md-3">
                <div class="text-muted">Nominal</div>
                <div class="fw-bold">R$ {{ formatMoney(negotiation.nominal_amount) }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Multa</div>
                <div class="fw-bold">R$ {{ formatMoney(negotiation.penalty_amount) }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Juros</div>
                <div class="fw-bold">R$ {{ formatMoney(negotiation.interest_amount) }}</div>
              </div>
              <div class="col-md-3">
                <div class="text-muted">Total negociado</div>
                <div class="fw-bold">R$ {{ formatMoney(negotiation.negotiated_amount) }}</div>
              </div>
            </div>
          </div>

          <div class="mb-4">
            <div class="fw-bold mb-2">Títulos de origem</div>
            <div v-if="originCharges.length === 0" class="text-muted">Nenhum título de origem encontrado.</div>
            <div v-else class="table-responsive">
              <table class="table table-striped align-middle">
                <thead>
                <tr>
                  <th>#</th>
                  <th>Referência</th>
                  <th>Vencimento</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Bill</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(c, idx) in originCharges" :key="c.id_charge">
                  <td>{{ idx + 1 }}</td>
                  <td>{{ c.charge_reference || '-' }}</td>
                  <td>{{ formatDate(c.charge_due_date) }}</td>
                  <td>R$ {{ formatMoney(c.charge_amount) }}</td>
                  <td><span :class="statusBadgeClass(c.charge_status)">{{ c.charge_status_dsc || c.charge_status }}</span></td>
                  <td>#{{ c.bill_to_receive?.id_bill_to_receive || '-' }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div class="fw-bold mb-2">Títulos de destino</div>
            <div v-if="destinationCharges.length === 0" class="text-muted">Nenhum título de destino encontrado.</div>
            <div v-else class="table-responsive">
              <table class="table table-striped align-middle">
                <thead>
                <tr>
                  <th>#</th>
                  <th>Referência</th>
                  <th>Vencimento</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Bill</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(c, idx) in destinationCharges" :key="c.id_charge">
                  <td>{{ idx + 1 }}</td>
                  <td>{{ c.charge_reference || '-' }}</td>
                  <td>{{ formatDate(c.charge_due_date) }}</td>
                  <td>R$ {{ formatMoney(c.charge_amount) }}</td>
                  <td><span :class="statusBadgeClass(c.charge_status)">{{ c.charge_status_dsc || c.charge_status }}</span></td>
                  <td>#{{ c.bill_to_receive?.id_bill_to_receive || '-' }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">Fechar</button>
        </div>
      </div>
    </div>
  </div>
</template>

